// TanStack Query hooks for freezes (T32). The task and evidence screens (lane D, wave 4) use
// useFrozenTask / useFrozenEvidence when the page URL has ?freeze=<id>.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  createFreezeApi,
  createHttp,
  newIdempotencyKey,
  type AfterFreeze,
  type FreezeDetail,
  type FreezeEntityType,
  type FreezeList,
  type FrozenView,
} from '@cis/api-client';

export const freezeApi = createFreezeApi(createHttp());

export const freezeKeys = {
  all: ['freezes'] as const,
  list: (page: number) => ['freezes', 'list', page] as const,
  detail: (id: number) => ['freezes', 'detail', id] as const,
  mark: (type: FreezeEntityType, key: string) => ['freezes', 'mark', type, key] as const,
  task: (id: number, taskId: string) => ['freezes', 'task', id, taskId] as const,
  evidence: (id: number, code: string) => ['freezes', 'evidence', id, code] as const,
};

// A frozen record never changes.
const FROZEN_STALE = Infinity;

export function useFreezeList(page: number): UseQueryResult<FreezeList> {
  return useQuery({
    queryKey: freezeKeys.list(page),
    queryFn: ({ signal }) => freezeApi.list(page, signal),
  });
}

export function useFreezeDetail(id: number | null): UseQueryResult<FreezeDetail> {
  return useQuery({
    queryKey: freezeKeys.detail(id ?? 0),
    queryFn: ({ signal }) => freezeApi.get(id ?? 0, signal),
    enabled: id !== null,
  });
}

export function useAfterFreeze(
  type: FreezeEntityType,
  key: string,
  enabled = true,
): UseQueryResult<AfterFreeze> {
  return useQuery({
    queryKey: freezeKeys.mark(type, key),
    queryFn: ({ signal }) => freezeApi.afterFreeze(type, key, signal),
    enabled,
  });
}

export function useFrozenTask(id: number | null, taskId: string): UseQueryResult<FrozenView> {
  return useQuery({
    queryKey: freezeKeys.task(id ?? 0, taskId),
    queryFn: ({ signal }) => freezeApi.frozenTask(id ?? 0, taskId, signal),
    enabled: id !== null,
    staleTime: FROZEN_STALE,
  });
}

export function useFrozenEvidence(id: number | null, code: string): UseQueryResult<FrozenView> {
  return useQuery({
    queryKey: freezeKeys.evidence(id ?? 0, code),
    queryFn: ({ signal }) => freezeApi.frozenEvidence(id ?? 0, code, signal),
    enabled: id !== null,
    staleTime: FROZEN_STALE,
  });
}

/** Owner: POST /freezes. One key per press, so a retried request creates one freeze. */
export function useCreateFreeze(): UseMutationResult<FreezeDetail, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (label: string) => freezeApi.create(label, newIdempotencyKey()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: freezeKeys.all }),
  });
}
